import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Link2, Users, Activity } from 'lucide-react';
import { useOnChainStats } from '../../hooks/useOnChainStats';

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    green: '#22c55e',
    red: '#ef4444',
    orange: '#FF8C00',
    blue: '#3b82f6',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(181, 111, 111, 0.2)',
};

const cardStyle: React.CSSProperties = {
    background: THEME.card,
    border: `1px solid ${THEME.border}`,
    borderRadius: '20px',
    overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
};

const bodyStyle: React.CSSProperties = {
    padding: '1.5rem',
};

const OnChainAnalytics: React.FC<{ tokenAddress?: string }> = ({ tokenAddress }) => {
    const stats = useOnChainStats(tokenAddress, 900_000);
    const [holderHistory, setHolderHistory] = useState<{ time: string; holders: number }[]>([]);

    // Reset accumulated history when token changes
    useEffect(() => {
        setHolderHistory([]);
    }, [tokenAddress]);

    const holders = stats?.holders || 0;
    const totalTransfers = stats?.totalTransfers || 0;
    const buys = stats?.buys24h || 0;
    const sells = stats?.sells24h || 0;
    const volume = stats?.volume24h || 0;

    // Track holder growth
    useEffect(() => {
        if (holders > 0) {
            setHolderHistory(prev => {
                const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const updated = [...prev, { time: now, holders }];
                return updated.slice(-20);
            });
        }
    }, [holders]);

    const buySellRatio = sells > 0 ? buys / sells : buys > 0 ? 2 : 1;

    // Buy/Sell bar data
    const buySellData = [
        { name: '24h Buys', value: buys, color: THEME.green },
        { name: '24h Sells', value: sells, color: THEME.red },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Link2 size={24} color={THEME.blue} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>On-Chain Analytics</h3>
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                {[
                    { label: 'Holders', value: holders.toLocaleString(), color: THEME.purple },
                    { label: 'Buy/Sell', value: buySellRatio.toFixed(2), color: buySellRatio > 1 ? THEME.green : THEME.red },
                    { label: 'Transfers', value: totalTransfers.toLocaleString(), color: THEME.blue },
                    { label: 'Volume 24h', value: `$${(volume / 1000).toFixed(1)}K`, color: THEME.yellow },
                ].map((stat, i) => (
                    <div key={i} style={{
                        background: `${stat.color}10`, border: `1px solid ${stat.color}25`,
                        borderRadius: '16px', padding: '1.25rem', textAlign: 'center',
                    }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: 800, color: stat.color }}>{stat.value}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Holder Growth */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <Users size={18} color={THEME.purple} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Holder Growth</h3>
                    </div>
                    <div style={bodyStyle}>
                        {holderHistory.length > 1 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={holderHistory}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="time" stroke="#6b7280" fontSize={10} />
                                    <YAxis stroke="#6b7280" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Line type="monotone" dataKey="holders" stroke={THEME.purple} strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>Collecting holder data...</p>
                        )}
                    </div>
                </div>

                {/* Buy/Sell Ratio */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <Activity size={18} color={THEME.green} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Buy/Sell (24h)</h3>
                    </div>
                    <div style={bodyStyle}>
                        {buys + sells > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={buySellData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                                    <YAxis stroke="#6b7280" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                        {buySellData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>Loading transaction data...</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Network Health */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <Link2 size={18} color={THEME.blue} />
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Network Activity</h3>
                </div>
                <div style={bodyStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {[
                            { label: 'Total Holders', value: holders.toLocaleString(), color: THEME.purple },
                            { label: 'Total Transfers', value: totalTransfers.toLocaleString(), color: THEME.blue },
                            { label: '24h Buys', value: buys.toString(), color: THEME.green },
                            { label: '24h Sells', value: sells.toString(), color: THEME.red },
                            { label: 'Buy/Sell Ratio', value: buySellRatio.toFixed(2), color: buySellRatio > 1 ? THEME.green : THEME.red },
                            { label: '24h Volume', value: `$${volume.toLocaleString()}`, color: THEME.yellow },
                        ].map((item, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
                            }}>
                                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{item.label}</span>
                                <span style={{ color: item.color, fontWeight: 700, fontSize: '0.875rem' }}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnChainAnalytics;
